import { CssBaseline } from '@mui/material';
import { styled } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';
import React, { useState, useRef, useEffect, type PropsWithChildren } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSound } from 'use-sound';

import routes from 'app/routes';
import { ConversationActionType, WebConversation, WSMsg } from 'app/models/ChatModels';
import Notifier from 'app/components/modules/common/Notifier';
import { Sleep } from 'app/utils/Sleep';
import analytics from 'app/services/analytics';

import { RouteItem } from '../../models/RouteModels';
import { useStores } from '../../stores/config/UseStores';
import ConfirmationDialog from '../modules/layout/ConfirmationDialog';
import Footer from '../modules/layout/Footer';
import Header from '../modules/layout/Header';
import Sidebar from '../modules/layout/Sidebar';

const DRAWER_WIDTH = 250;
const COLLAPSED_DRAWER_WIDTH = 70;

const AppContent = styled('div')(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  width: `calc(100% - ${DRAWER_WIDTH}px)`,
  transition: theme.transitions.create(['width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

const MainContent = styled('div')`
  flex: 1;
  background: '#fafafa';
`;

const Root = styled('div')`
  display: flex;
  min-height: 100vh;
  overflow-x: hidden;
`;

type DashboardLayoutProps = PropsWithChildren;

const DashboardLayout = observer(({ children }: DashboardLayoutProps) => {
  const navigate = useHistory();
  const location = useLocation();
  const {
    dataStores: { authStore, chatsStore, userStore },
    uiStores: { notifierStore, dialogStore },
  } = useStores();
  analytics.initialize();
  const [open, setOpen] = useState(false);
  const [routesState, setRoutesState] = useState<RouteItem[]>(routes);
  const [busy, setBusy] = useState(false);
  const [notifyWeb] = useSound(userStore.webNotificationSound, {
    preload: true,
  });
  const [notifySMS] = useSound(userStore.smsNotificationSound, {
    preload: true,
  });

  const soundWeb = useRef(null);
  const soundSms = useRef(null);

  // @ts-ignore
  soundWeb.current = notifyWeb;
  // @ts-ignore
  soundSms.current = notifySMS;

  const smsChatsStore = chatsStore.smsChatsStore;
  const webChatsStore = chatsStore.webChatsStore;

  useEffect(() => {
    if (authStore.authData.access) {
      //init chats and notifications

      (async () => {
        await Sleep(authStore.expTimeInMs - 5000).then(() => authStore.refresh());
      })();

      chatsStore.openCommunicationsChannel(authStore.authData.access, true);

      return () => {
        smsChatsStore.closeWSNotifications();
        webChatsStore.closeWSNotifications();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStore.authData.access]);

  useEffect(() => {
    if (smsChatsStore.wsNotificationMessage) {
      const message: WSMsg = smsChatsStore.wsNotificationMessage;

      if (
        message.action === ConversationActionType.NewMessage &&
        smsChatsStore.activeConversationSid !== message.conversation_sid &&
        message.source === 'survey_conversation'
      ) {
        if (userStore.userProfileData.sms_chat_sound_enable) {
          // @ts-ignore
          soundSms.current();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smsChatsStore.wsNotificationMessage]);

  useEffect(() => {
    if (webChatsStore.wsNotificationMessage) {
      const message: WSMsg = webChatsStore.wsNotificationMessage;
      if (
        message.action === ConversationActionType.NewMessage &&
        webChatsStore.chats.activeConversationSid !== message.conversation_sid &&
        message.source === 'web_conversation'
      ) {
        analytics.captureChatNotification(message.data as WebConversation);
        if (userStore.userProfileData.web_chat_sound_enable) {
          // @ts-ignore
          soundWeb.current();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webChatsStore.wsNotificationMessage]);

  useEffect(() => {
    const chatRoute = routesState.find((r) => r.path === '/web-chats')!;
    chatRoute.counter = webChatsStore?.unreadMsgCount?.all;
    setRoutesState([...routesState]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webChatsStore?.unreadMsgCount]);

  useEffect(() => {
    const chatRoute = routesState.find((r) => r.path === '/chats')!;
    chatRoute.counter = smsChatsStore?.unreadMsgCount;
    setRoutesState([...routesState]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smsChatsStore?.unreadMsgCount]);

  useEffect(() => {
    (async () => await handleLocationChange())();

    if (location.pathname === '/chats') {
      const chatRoute = routesState.find((r) => r.path === '/chats')!;
      if (chatRoute.id !== 'SMS Texts' && !smsChatsStore.hasUnread) {
        chatRoute.id = 'SMS Texts';
        setRoutesState([...routesState]);
      }
    }

    if (location.pathname === '/web-chats') {
      const chatRoute = routesState.find((r) => r.path === '/web-chats')!;
      if (chatRoute.id !== 'Web Chats' && !webChatsStore.hasUnread) {
        chatRoute.id = 'Web Chats';
        setRoutesState([...routesState]);
      }
    }

    const sid = new URLSearchParams(location.search).get('sid');
    if (!sid) {
      smsChatsStore.unblockChat();
    }
    webChatsStore.unblockChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (authStore.isAuth && authStore.isConcierge) {
      (async () => {
        await userStore.getConciergeProfileData();
        await userStore.loadSoundNotifications();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStore?.authData?.access]);

  const handleLocationChange = async () => {
    setBusy(true);
    if (!authStore.isAuth) {
      await authStore.refresh();
    }
    setBusy(false);
  };

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  return (
    <Root>
      <CssBaseline />
      <Sidebar
        routes={routesState}
        open={open}
        onClose={handleDrawerToggle}
      />
      <AppContent
        sx={{
          width: open ? `calc(100% - ${DRAWER_WIDTH}px)` : `calc(100% - ${COLLAPSED_DRAWER_WIDTH}px)`,
          overflowX: 'scroll',
        }}
      >
        <Header
          onLogout={() => authStore.logout()}
          onProfile={() => navigate.push('/profile')}
        />
        <MainContent>{busy ? 'Loading...' : children}</MainContent>
        <Footer />
      </AppContent>
      <Notifier
        {...notifierStore.notifier}
        onClose={notifierStore.resetNotifier}
      />
      <ConfirmationDialog
        {...dialogStore.dialog}
        onClose={dialogStore.dialog.onClose}
      />
    </Root>
  );
});

export default DashboardLayout;
